/**
 * OutreachPanel — Step 3 rich detail.
 *
 * Demo-grade message-thread mockup. Pulls draft inquiry email seeded
 * from Phase 3 sourcing output; lets the user fake "send" by copying
 * to clipboard.
 */
import React, { useState } from 'react';
import { Send, Copy, MessageSquare, Mail } from 'lucide-react';

const buildDraftEmail = (order, factory) => {
  return `Subject: ${order.contractTitle} — RFQ for ${order.quantity?.toLocaleString()} units

Hello ${factory.name} team,

We've been awarded a contract by ${order.buyer} for ${order.quantity?.toLocaleString()} units of: ${order.productSummary}

We've reviewed your listings and would like to invite you to quote.

Required (per our spec sheet, attached):
${order.techSpec?.sections?.slice(0, 3).map((s) => `  • ${s.heading}: ${s.body}`).join('\n') || ''}

Please confirm:
  1. Unit price for ${order.quantity?.toLocaleString()} units (FOB and DDP if available)
  2. Lead time from PO confirmation
  3. Confirmation that the certifications listed in our spec are met
  4. Any quantity-break pricing
  5. Payment terms (we typically use Alibaba Trade Assurance / wire)

Delivery destination: ${order.deliveryRegion}, Canada.
Target arrival: 45 days from PO.

Looking forward to your response.

Best,
[Procurement team]`;
};

const OutreachPanel = ({ order }) => {
  const [activeFactoryId, setActiveFactoryId] = useState(order.factories?.[0]?.id);
  const [copied, setCopied] = useState(null);

  const candidates = (order.factories || []).filter((f) => f.status !== 'passed');
  const factory = candidates.find((f) => f.id === activeFactoryId) || candidates[0];

  const draft = factory ? buildDraftEmail(order, factory) : '';

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(draft);
      setCopied(factory.id);
      setTimeout(() => setCopied(null), 1500);
    } catch (e) {
      console.warn('Clipboard write failed', e);
    }
  };

  return (
    <div className="manifest-rich-panel manifest-outreach">
      <div className="manifest-rich-panel-head">
        <div className="manifest-rich-panel-title">
          <MessageSquare size={16} /> Outreach drafts
        </div>
      </div>

      <div className="manifest-outreach-layout">
        <div className="manifest-outreach-list">
          {candidates.map((f) => (
            <button
              key={f.id}
              className={`manifest-outreach-list-item ${activeFactoryId === f.id ? 'active' : ''} status-${f.status}`}
              onClick={() => setActiveFactoryId(f.id)}
            >
              <div className="manifest-outreach-list-name">{f.name}</div>
              <div className="manifest-outreach-list-meta">
                <Mail size={10} /> {f.contact}
              </div>
              <div className="manifest-outreach-list-status">{f.status}</div>
            </button>
          ))}
        </div>

        <div className="manifest-outreach-preview">
          <div className="manifest-outreach-preview-head">
            <span><strong>To:</strong> {factory?.contact}</span>
            <button className="manifest-btn-ghost" onClick={handleCopy}>
              <Copy size={14} /> {copied === factory?.id ? 'Copied' : 'Copy email'}
            </button>
            <button className="manifest-btn-ghost">
              <Send size={14} /> Mark as sent
            </button>
          </div>
          <pre className="manifest-outreach-body">{draft}</pre>
        </div>
      </div>
    </div>
  );
};

export default OutreachPanel;
